import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { Database, Download, ShieldAlert, ArrowLeft } from 'lucide-react';

const BackupTool: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({ count: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/admin');
        return;
      }
      
      // Verificar si es admin
      try {
        const adminSnap = await getDocs(collection(db, 'admins'));
        const isAdminUser = adminSnap.docs.some(doc => doc.id === user.uid);
        
        if (!isAdminUser) {
          alert("Acesso negado. Apenas administradores podem usar esta ferramenta.");
          navigate('/');
          return;
        }

        setIsAdmin(true);
        // Contar participantes para el resumen
        const partSnap = await getDocs(collection(db, 'participantes'));
        setStats({ count: partSnap.size });
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    });

    return () => checkAuth();
  }, [navigate]);

  const downloadBackup = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'participantes'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GIEPEI_Backup_Participantes_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      alert(`Backup de ${data.length} participantes concluído com sucesso!`);
    } catch (err) {
      alert("Erro ao gerar backup.");
      console.error(err);
    }
    setLoading(false);
  };

  if (loading) return <div style={{padding: '10rem', textAlign: 'center'}}>Iniciando ferramenta de segurança...</div>;
  if (!isAdmin) return null;

  return (
    <div style={{ padding: '4rem 2rem', backgroundColor: '#f4f7f6', minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', background: 'white', padding: '3rem', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{ width: '64px', height: '64px', background: '#e8f5e9', color: '#2e7d32', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <Database size={32} />
          </div>
          <h1 style={{ fontSize: '1.8rem', color: '#1a1a1a', margin: '0 0 0.5rem 0' }}>GIEPEI Backup Tool</h1>
          <p style={{ color: '#666', fontSize: '0.95rem' }}>Ferramenta oficial para extração e salvaguarda de dados.</p>
        </div>

        <div style={{ background: '#fff9e6', border: '1px solid #ffe58f', padding: '1.5rem', borderRadius: '8px', marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <ShieldAlert style={{ color: '#d4a017', flexShrink: 0 }} size={24} />
            <div>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>Aviso de Segurança</h4>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#856404', lineHeight: '1.5' }}>
                Este ficheiro contém informações sensíveis de {stats.count} participantes. Armazene o backup num local seguro e cifrado.
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <button 
            onClick={downloadBackup}
            disabled={loading}
            style={{ 
              width: '100%', 
              padding: '1.2rem', 
              background: '#2e7d32', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              fontWeight: 'bold', 
              fontSize: '1.1rem', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.8rem',
              transition: 'background 0.2s'
            }}
          >
            <Download size={20} />
            Descarregar Backup JSON ({stats.count})
          </button>

          <button 
            onClick={() => navigate('/admin')}
            style={{ 
              width: '100%', 
              padding: '1rem', 
              background: 'transparent', 
              color: '#666', 
              border: '1px solid #ddd', 
              borderRadius: '6px', 
              fontWeight: '600', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.8rem'
            }}
          >
            <ArrowLeft size={18} />
            Voltar ao Painel
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: '3rem', fontSize: '0.75rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '1px' }}>
          Protegido por BRIDNOLOGY Security
        </p>
      </div>
    </div>
  );
};

export default BackupTool;
